import styled from '@emotion/styled';
import { AxiosError } from 'axios';
import { FormikHelpers } from 'formik';
import { TokenData } from 'modules/application-link/domain/interfaces/token-data';
import applicationsResource from 'modules/application-link/domain/resources/applications.resource';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import UDText from 'ud-ui/components/text';
import ApplicationLinkForm, { IFormValues } from '../components/application-link-form';
import Layout from '../components/layout';
import ApplicationExpiredPage from './application-expired-page';
import { theme } from 'styles/theme/theme';

const ApplicationLinkPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const currentDate = Date.now();

  const {
    data: tokenData,
    isLoading,
    error,
  } = useQuery<TokenData | undefined>({
    queryKey: ['token', token],
    queryFn: async () => {
      if (!token) return undefined;
      return applicationsResource.decodeToken(token);
    },
    retry: false,
  });

  const submit = async (values: IFormValues, formikHelpers: FormikHelpers<IFormValues>) => {
    try {
      if (!token) {
        toast.error('Ошибка, запросите новую ссылку у организатора');
        console.error('Token is not found');
        return;
      }

      const photo = values.photo?.[0] instanceof File ? (values.photo[0] as File) : undefined;

      if (!values.position) {
        toast.error('Укажите амплуа');
        return;
      }

      await applicationsResource.createFromToken({
        name: values.name,
        surname: values.surname,
        middleName: values.patronymic || undefined,
        birthDate: values.birthday,
        token: token,
        number: Number(values.number),
        position: values.position.value,
        photo,
      });

      toast.success('Заявка отправлена');
      formikHelpers.resetForm();
      navigate('/success');
    } catch (error) {
      toast.error('Произошла ошибка при отправке заявки');
      console.error('Error submitting application:', error);
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  const expiredMilliseconds = tokenData?.exp ? tokenData.exp * 1000 : undefined;
  const expiredError = error && (error as AxiosError).response?.data.message === 'Internal error: Expired JWT Token';
  if ((expiredMilliseconds && expiredMilliseconds < currentDate) || expiredError) {
    return <ApplicationExpiredPage />;
  }

  if (error) {
    return <div>Произошла ошибка при получении данных, обратитесь к организатору для получения новой ссылки</div>;
  }

  if (!token) {
    return <div>Токен не найден, обратитесь к организатору для получения новой ссылки</div>;
  }

  return (
    <Layout>
      <UDText id="title" type="title-1" style={{ fontWeight: 700 }} color="white" className="mb-9">
        Онлайн заявка на участие в турнире
      </UDText>
      {
        <div id="organizer" className="justify-content: center mb-5 flex flex-row items-center gap-3">
          {isLoading ? (
            <SkeletonBlock className="skeleton-block" style={{ width: 220, height: 54, borderRadius: 8 }} />
          ) : (
            <>
              <img
                alt="organizer-logo"
                src={tokenData?.tournamentLogoUrl ?? USER_DEFAULT_AVATAR}
                id="organizer-logo"
                style={{
                  border: `1px solid ${theme.colors.SurfaceRomanSilver30}`,
                  width: 54,
                  height: 54,
                  objectFit: 'contain',
                  borderRadius: 8,
                }}
              />
              <UDText id="organizer-name" type="title-1" style={{ fontWeight: 500, fontSize: 26 }} color="white">
                {tokenData?.tournamentName ?? 'Неизвестный организатор'}
              </UDText>
            </>
          )}
        </div>
      }
      <div id="tournament" className="mb-5 flex flex-col items-center justify-center gap-2">
        <UDText id="tournament-title" type="subhead" style={{ fontWeight: 400 }} color={theme.colors.SurfaceRomanSilver30}>
          Турнир
        </UDText>
        {isLoading ? (
          <SkeletonBlock className="skeleton-block" style={{ width: 220, height: 34, borderRadius: 8 }} />
        ) : (
          <UDText id="tournament-name" type="title-1" style={{ fontWeight: 500, fontSize: 20 }} color="white">
            {tokenData?.championshipName ?? 'Неизвестный турнир'}
          </UDText>
        )}
      </div>
      <div id="team" className="mb-7 flex flex-col items-center justify-center gap-2">
        <UDText id="team-title" type="subhead" style={{ fontWeight: 400 }} color={theme.colors.SurfaceRomanSilver30}>
          Ваша команда
        </UDText>
        {isLoading ? (
          <SkeletonBlock className="skeleton-block" style={{ width: 220, height: 34, borderRadius: 8 }} />
        ) : (
          <UDText id="team-name" type="title-1" style={{ fontWeight: 500, fontSize: 20 }} color="white">
            {tokenData?.teamName ?? 'Неизвестная команда'}
          </UDText>
        )}
      </div>
      <ApplicationLinkForm onSubmit={submit} isLoading={isLoading} />
    </Layout>
  );
};

export default ApplicationLinkPage;

export const SkeletonBlock = styled.div`
  width: 100%;
  height: 20px;

  background: linear-gradient(110deg, #4b4b68 3%, #25254e 60%, #4b4b68 97%);
  background-size: 200% 100%;
`;
